/**
 * 効果音。**アセット無しで鳴らせる WebAudio 合成SE**。
 *
 * 音源ファイルはまだ無い(docs/05_asset_pipeline.md は Phase 2)ので、
 * オシレータ+エンベロープで太鼓の達人風の短いSEを合成する。
 * WebAudio が使えない環境(SSR・jsdom・古い端末)では全メソッドが無害な no-op になる。
 *
 * - 正解: 短いクリック(高めの矩形波を数十ms)
 * - ミス : 低いブザー(のこぎり波+デチューン)
 * - 単語完成/クリア: 上昇アルペジオ
 * - フィーバー開始: キラキラ(高音のランダム散らし)
 */

export type SoundName = 'correct' | 'miss' | 'wordComplete' | 'clear' | 'fever' | 'ui';

type AudioContextCtor = new () => AudioContext;

/** 単語音声の再生に必要な最小インターフェース(テストで差し替える)。 */
export interface VoiceElement {
  volume: number;
  currentTime: number;
  /** 再生し終わったときの通知(HTMLAudioElement.onended 互換)。 */
  onended: ((...args: never[]) => unknown) | null;
  play(): Promise<void> | void;
  pause(): void;
}

export type VoiceFactory = (url: string) => VoiceElement | null;

/**
 * 単語音声の鳴らしかた。
 *
 * - `interrupt`: 鳴っている音声を止めて即座に鳴らす。保留中のキューも捨てる。
 * - `queue`   : 何も鳴っていなければ即座に。鳴っていれば終わってから鳴らす(保留は最新1件だけ)。
 */
export type VoicePlayMode = 'interrupt' | 'queue';

function resolveVoiceElement(url: string): VoiceElement | null {
  if (typeof Audio === 'undefined') return null;
  return new Audio(url);
}

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export class SoundService {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private volume = 0.6;
  private muted = false;
  private failed = false;
  /** いま鳴っている単語音声(重ね鳴り防止のため1本だけ持つ)。 */
  private voice: VoiceElement | null = null;
  /** 現在の音声が終わったら鳴らす1件(新しいものが来たら置き換える)。 */
  private queued: string | null = null;
  /** `waitVoiceEnd()` の待ち手。単語音声が鳴り止んだ時点でまとめて解決する。 */
  private voiceWaiters: (() => void)[] = [];

  constructor(
    private readonly ctorProvider: () => AudioContextCtor | null = resolveAudioContextCtor,
    private readonly voiceFactory: VoiceFactory = resolveVoiceElement,
  ) {}

  /** ユーザー操作のタイミングで呼ぶ(自動再生ポリシー対策)。 */
  unlock(): void {
    const ctx = this.ensureContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume));
    this.applyGain();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    // 単語音声は master gain を通らない(<audio> 直再生)ので、明示的に止める。
    if (muted) this.stopVoice();
    this.applyGain();
  }

  isAvailable(): boolean {
    return this.ensureContext() !== null;
  }

  play(name: SoundName): void {
    const ctx = this.ensureContext();
    if (!ctx || !this.master || this.muted || this.volume <= 0) return;
    const t = ctx.currentTime;
    switch (name) {
      case 'correct':
        this.blip(t, 880, 0.045, 'square', 0.25);
        break;
      case 'miss':
        this.blip(t, 110, 0.18, 'sawtooth', 0.3);
        this.blip(t, 104, 0.18, 'sawtooth', 0.22);
        break;
      case 'wordComplete':
        this.arpeggio(t, [660, 880, 1100], 0.06, 0.22);
        break;
      case 'clear':
        this.arpeggio(t, [523, 659, 784, 1047, 1319], 0.09, 0.28);
        break;
      case 'fever':
        this.sparkle(t);
        break;
      case 'ui':
        this.blip(t, 620, 0.06, 'triangle', 0.22);
        break;
      /* v8 ignore next 2 */
      default:
        break;
    }
  }

  /**
   * 単語の発音音声。アセット未配備(url=null)ならスキップする。
   * 失敗しても握りつぶす(音声なしで劣化なく遊べること)。
   *
   * 単語音声は**常に1本だけ**鳴らす。重なりの解決は mode で選ぶ:
   *
   * - `interrupt`(既定): 打ち終わりの再生・手動の再生ボタン。鳴っている音声を止めて即座に鳴らす。
   * - `queue`: 出題時の再生。engine は打ち終わり(wordCompleted)の直後に**同期で**次の単語を
   *   出題する(packages/engine の completeWord)。ここで割り込むと打ち終わりの音声が
   *   一瞬も鳴らないので、現在の音声が終わってから鳴らす。保留は最新1件だけで、
   *   次の割り込み再生が来たら捨てる。
   *
   * 効果音(SE)は WebAudio 側なので影響を受けない。
   */
  playVoice(url: string | null, mode: VoicePlayMode = 'interrupt'): void {
    if (!url || this.muted) return;
    if (mode === 'queue' && this.voice) {
      this.queued = url;
      return;
    }
    this.startVoice(url);
  }

  /**
   * いま鳴っている単語音声が終わるのを待つ(`onended` ベース)。
   *
   * 何も鳴っていなければ即座に解決する。`stopVoice()`・再生失敗でも解決するので、
   * 自動再生がブロックされた場合でも待ちっぱなしにはならない。
   * 保留(queue)の音声が続けて鳴る場合は、そちらが終わるまで待つ
   * (「単語音声が鳴り止むまで」という意味)。
   * ただし「音声が長い」「onended が来ない」端末はあり得るので、
   * 呼び出し側で必ず上限時間を掛けること(logic/resultTransition.ts)。
   */
  waitVoiceEnd(): Promise<void> {
    if (!this.voice) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.voiceWaiters.push(resolve);
    });
  }

  /** 鳴っている単語音声を止め、保留中のキューも捨てる。 */
  stopVoice(): void {
    this.silence();
    this.settleVoiceWaiters();
  }

  /** 停止だけ(待ち手の解決はしない)。続けて別の音声を鳴らす場合に使う。 */
  private silence(): void {
    this.queued = null;
    const audio = this.voice;
    if (!audio) return;
    this.voice = null;
    try {
      audio.onended = null;
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // 停止できない環境では無視する。
    }
  }

  private startVoice(url: string): void {
    this.silence();
    try {
      const audio = this.voiceFactory(url);
      if (!audio) return;
      audio.volume = this.volume;
      audio.onended = () => this.handleVoiceEnded(audio);
      this.voice = audio;
      void Promise.resolve(audio.play()).catch(() => this.handleVoiceFailed(audio));
    } catch {
      // 再生できない環境では無視する。
      this.voice = null;
      this.settleVoiceWaiters();
    }
  }

  private handleVoiceEnded(audio: VoiceElement): void {
    // 既に別の音声へ差し替わっていれば、古い通知は捨てる。
    if (this.voice !== audio) return;
    audio.onended = null;
    this.voice = null;
    const next = this.queued;
    this.queued = null;
    // 続けて鳴らすものがあるなら「まだ鳴り止んでいない」ので待ち手は解決しない。
    if (next && !this.muted) this.startVoice(next);
    this.settleVoiceWaiters();
  }

  /** 自動再生ポリシー等で再生できなかった場合。保留を積んだまま詰まらせない。 */
  private handleVoiceFailed(audio: VoiceElement): void {
    if (this.voice !== audio) return;
    audio.onended = null;
    this.voice = null;
    this.queued = null;
    this.settleVoiceWaiters();
  }

  /** 単語音声が鳴っていなければ待ち手をまとめて解決する。 */
  private settleVoiceWaiters(): void {
    if (this.voice || this.voiceWaiters.length === 0) return;
    const waiters = this.voiceWaiters;
    this.voiceWaiters = [];
    for (const resolve of waiters) resolve();
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (this.failed) return null;
    const Ctor = this.ctorProvider();
    if (!Ctor) {
      this.failed = true;
      return null;
    }
    try {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
      this.applyGain();
      return this.ctx;
    } catch {
      this.failed = true;
      return null;
    }
  }

  private applyGain(): void {
    if (!this.master || !this.ctx) return;
    this.master.gain.setValueAtTime(this.muted ? 0 : this.volume, this.ctx.currentTime);
  }

  private blip(
    at: number,
    frequency: number,
    duration: number,
    type: OscillatorType,
    peak: number,
  ): void {
    const ctx = this.ctx;
    const master = this.master;
    /* v8 ignore next */
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, at);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  private arpeggio(at: number, notes: readonly number[], step: number, peak: number): void {
    notes.forEach((frequency, index) => {
      this.blip(at + index * step, frequency, step * 1.6, 'triangle', peak);
    });
  }

  private sparkle(at: number): void {
    for (let i = 0; i < 10; i++) {
      const frequency = 1200 + Math.random() * 1800;
      this.blip(at + i * 0.035, frequency, 0.12, 'sine', 0.16);
    }
  }
}

/** アプリ全体で共有するSE。 */
export const sound = new SoundService();
